/**
 * Nurture Content Library — pure helpers for the Marketing → Nurture
 * Content page (2026-10-28). Kept free of React so jest can test them.
 *
 * The backend (`backend/nurture_content.py`) is the authority on lint
 * flags and sendability; these helpers only present what it returns.
 */

export const STATUS_TABS = [
  { key: "draft", label: "Drafts", help: "Written by Pulse AI or an operator. Never sent." },
  { key: "approved", label: "Approved", help: "Approved by an admin. Eligible to be sent." },
  { key: "active", label: "Active", help: "Approved and already used in at least one send." },
  { key: "rejected", label: "Rejected", help: "Turned down in review. Never sent." },
  { key: "retired", label: "Retired", help: "Taken out of rotation. Never sent again." },
  { key: "", label: "All", help: "Every item in the library." },
];

export const STATUS_STYLE = {
  draft: { label: "Draft", cls: "bg-[#F5F5F5] text-[#525252] border-[#E5E5E5]" },
  approved: { label: "Approved", cls: "bg-[#ECFDF5] text-[#047857] border-[#A7F3D0]" },
  active: { label: "Active", cls: "bg-[#EFF6FF] text-[#1D4ED8] border-[#BFDBFE]" },
  rejected: { label: "Rejected", cls: "bg-[#FEF2F2] text-[#B91C1C] border-[#FECACA]" },
  retired: { label: "Retired", cls: "bg-[#FAFAFA] text-[#A3A3A3] border-[#E5E5E5]" },
};

export const SENDABLE = ["approved", "active"];

export function isSendable(status) {
  return SENDABLE.includes(String(status || "").toLowerCase());
}

/** Body words excluding the greeting line (mirrors the backend count). */
export function wordCount(bodyText) {
  const text = String(bodyText || "").trim();
  if (!text) return 0;
  const [first, ...rest] = text.split("\n");
  const isGreeting = /^\s*(hi|hello|dear|good (morning|afternoon))\b/i.test(first) &&
    first.trim().split(/\s+/).length <= 6;
  const msg = isGreeting ? rest.join("\n") : text;
  const words = msg.trim().match(/\S+/g);
  return words ? words.length : 0;
}

/** v2: words of copy before the footer (opening + feature + CTA + cross-sell),
 * mirroring backend `nurture_content.copy_word_count`. */
export const COPY_FIELDS = ["opening", "feature", "cta_label", "cross_sell"];
export function copyWordCount(item) {
  return COPY_FIELDS.reduce((n, f) => n + ((String(item?.[f] || "").trim().match(/\S+/g) || []).length), 0);
}

export const WORD_RANGE = [60, 110];

export const PREVIEW_WIDTHS = { desktop: 640, mobile: 375 };

/** Body preview without the greeting, cut to `max` characters. */
export function preview(bodyText, max = 160) {
  const text = String(bodyText || "").trim();
  const lines = text.split("\n");
  const body = /^\s*(hi|hello|dear)\b/i.test(lines[0] || "") ? lines.slice(1).join(" ") : text;
  const flat = body.replace(/\s+/g, " ").trim();
  return flat.length > max ? flat.slice(0, max - 1).trimEnd() + "…" : flat;
}

export function blockingFlags(item) {
  return (item?.lint_flags || []).filter((f) => f?.severity === "block");
}

export function warningFlags(item) {
  return (item?.lint_flags || []).filter((f) => f?.severity !== "block");
}

/**
 * Split a selection for bulk approval, mirroring the backend rule: only
 * drafts; drafts with blocking lint flags only when overriding.
 */
export function bulkApprovalPlan(items, selectedIds, { override = false } = {}) {
  const chosen = (items || []).filter((i) => (selectedIds || []).includes(i.id));
  const approvable = [];
  const skipped = [];
  for (const it of chosen) {
    if (it.status !== "draft") skipped.push({ id: it.id, reason: `already ${it.status}` });
    else if (blockingFlags(it).length && !override) skipped.push({ id: it.id, reason: "has blocking lint flags" });
    else approvable.push(it.id);
  }
  return { approvable, skipped };
}

export function rate(n, d) {
  if (!d) return "—";
  return `${Math.round((100 * (n || 0)) / d)}%`;
}

/** Error text for a failed request: the server's own message when it gave one. */
export function errorText(err, fallback) {
  const detail = err?.response?.data?.detail;
  if (typeof detail === "string" && detail.trim()) return detail;
  if (err?.code === "ECONNABORTED" || /timeout/i.test(err?.message || "")) {
    return "The request timed out. Nothing was confirmed — refresh to check before retrying.";
  }
  if (err?.response?.status === 403) return "You don't have permission to do that.";
  return fallback;
}
